import { KeyboardEvent } from "react";

/**
 * Keyboard activation for click-handling elements that are not native buttons:
 * fires the handler on Enter or Space, the keys a real <button> responds to.
 * Pair with role="button" and tabIndex={0}.
 *
 * Prefer a native <button> in new code — this exists for legacy markup where
 * switching the element would change its styling (the global button/.btn
 * rules key off the tag).
 */
export function activateOnKeys(handler: () => void) {
  return (evt: KeyboardEvent) => {
    if (evt.key === "Enter" || evt.key === " ") {
      evt.preventDefault();
      handler();
    }
  };
}

export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keeps Tab cycling inside a modal container: call from the container's
 * keydown handler when evt.key === "Tab". Wraps focus at either edge.
 * Shared by ui/Dialog and the legacy AppDialog base.
 */
export function trapTabKey(
  container: HTMLElement | null,
  evt: { shiftKey: boolean; preventDefault(): void }
) {
  if (!container) {
    return;
  }
  const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  if (focusable.length === 0) {
    evt.preventDefault();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (evt.shiftKey && (active === first || active === container)) {
    evt.preventDefault();
    last.focus();
  } else if (!evt.shiftKey && active === last) {
    evt.preventDefault();
    first.focus();
  }
}
